// Journey Lab orchestration. These focused hooks are the single owners of catalogue loading, run selection,
// scripted replay admission, and the live-detail refresh loop. A run is read once immediately, then exactly
// once per second only while its server-authored state is queued/running. Terminal, failed, switched, and
// unmounted reads leave no timer behind. No hook infers execution mode from provider/model fields.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type {
  JourneyCatalogueView,
  JourneyLabApi,
  JourneyRunState,
  JourneyRunView,
} from './journey-lab-api.ts';

export const JOURNEY_ACTIVE_POLL_MS = 1_000;

interface RunController {
  readonly selectedRun: JourneyRunView | null;
  readonly selectedRunId: string | null;
  readonly selectionRef: MutableRefObject<string | null>;
  readonly runLoading: boolean;
  readonly runError: boolean;
  readonly selectRun: (runId: string) => void;
}

interface CatalogueController {
  readonly catalogue: JourneyCatalogueView | null;
  readonly catalogueLoading: boolean;
  readonly catalogueError: boolean;
  readonly reloadCatalogue: () => void;
}

export interface JourneyLabController extends Omit<RunController, 'selectionRef'> {
  readonly catalogue: JourneyCatalogueView | null;
  readonly catalogueLoading: boolean;
  readonly catalogueError: boolean;
  readonly replayingScenarioId: string | null;
  readonly replayError: boolean;
  readonly replay: (scenarioId: string) => Promise<void>;
  /** Select a newly admitted live run and refresh the catalogue even if the rehearsal finished instantly. */
  readonly selectStartedRun: (runId: string) => void;
}

interface RunLoadState {
  readonly run: JourneyRunView | null;
  readonly loading: boolean;
  readonly error: boolean;
}

function isActive(state: JourneyRunState): boolean {
  return state === 'queued' || state === 'running';
}

/** Schedule the next detail read only for the selected active snapshot. */
function useActiveRunPolling(
  run: JourneyRunView | null,
  selectedRunId: string | null,
  loadRun: (runId: string, showLoading: boolean) => Promise<void>,
): void {
  useEffect(() => {
    if (
      selectedRunId === null ||
      run === null ||
      run.runId !== selectedRunId ||
      !isActive(run.state)
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      void loadRun(selectedRunId, false);
    }, JOURNEY_ACTIVE_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [loadRun, run, selectedRunId]);
}

/** Own selected-run identity, immediate detail load, stale-response fencing, and active polling. */
function useRunController(api: JourneyLabApi): RunController {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const selectionRef = useRef<string | null>(null);
  const [state, setState] = useState<RunLoadState>({ run: null, loading: false, error: false });
  const selectRun = useCallback((runId: string): void => {
    if (selectionRef.current === runId) return;
    selectionRef.current = runId;
    setSelectedRunId(runId);
    setState({ run: null, loading: true, error: false });
  }, []);
  const loadRun = useCallback(
    async (runId: string, showLoading: boolean): Promise<void> => {
      if (showLoading) setState({ run: null, loading: true, error: false });
      try {
        const next = await api.getJourneyRun(runId);
        if (selectionRef.current === runId) {
          setState({ run: next.run, loading: false, error: false });
        }
      } catch {
        if (selectionRef.current === runId) setState({ run: null, loading: false, error: true });
      }
    },
    [api],
  );
  useEffect(() => {
    if (selectedRunId !== null) void loadRun(selectedRunId, true);
  }, [loadRun, selectedRunId]);
  useEffect(() => () => void (selectionRef.current = null), []);
  useActiveRunPolling(state.run, selectedRunId, loadRun);
  return {
    selectedRun: state.run,
    selectedRunId,
    selectionRef,
    runLoading: state.loading,
    runError: state.error,
    selectRun,
  };
}

interface CatalogueLoadState {
  readonly view: JourneyCatalogueView | null;
  readonly loading: boolean;
  readonly error: boolean;
}

/** Own catalogue loading and first-latest-run selection; background refresh preserves the current view. */
function useCatalogueController(api: JourneyLabApi, run: RunController): CatalogueController {
  const [state, setState] = useState<CatalogueLoadState>({
    view: null,
    loading: true,
    error: false,
  });
  const loadCatalogue = useCallback(
    async (showLoading: boolean): Promise<void> => {
      if (showLoading) setState((current) => ({ ...current, loading: true, error: false }));
      try {
        const view = await api.getJourneys();
        setState({ view, loading: false, error: false });
        if (run.selectionRef.current === null) {
          const firstLatest = view.journeys.find((journey) => journey.latestRun !== undefined);
          if (firstLatest?.latestRun !== undefined) run.selectRun(firstLatest.latestRun.runId);
        }
      } catch {
        setState((current) => ({ ...current, loading: false, error: true }));
      }
    },
    [api, run.selectRun, run.selectionRef],
  );
  useEffect(() => void loadCatalogue(true), [loadCatalogue]);
  const reloadCatalogue = useCallback(() => void loadCatalogue(false), [loadCatalogue]);
  return {
    catalogue: state.view,
    catalogueLoading: state.loading,
    catalogueError: state.error,
    reloadCatalogue,
  };
}

interface ReplayController {
  readonly replayingScenarioId: string | null;
  readonly replayError: boolean;
  readonly replay: (scenarioId: string) => Promise<void>;
}

/** Own the single, non-retried replay mutation and select its returned durable run. */
function useReplayController(
  api: JourneyLabApi,
  selectRun: RunController['selectRun'],
  reloadCatalogue: CatalogueController['reloadCatalogue'],
): ReplayController {
  const [replayingScenarioId, setReplayingScenarioId] = useState<string | null>(null);
  const replayFence = useRef(false);
  const [replayError, setReplayError] = useState(false);
  const replay = useCallback(
    async (scenarioId: string): Promise<void> => {
      if (replayFence.current) return;
      replayFence.current = true;
      setReplayingScenarioId(scenarioId);
      setReplayError(false);
      try {
        const accepted = await api.replayJourney(scenarioId);
        selectRun(accepted.runId);
        reloadCatalogue();
      } catch {
        setReplayError(true);
        reloadCatalogue();
      } finally {
        replayFence.current = false;
        setReplayingScenarioId(null);
      }
    },
    [api, reloadCatalogue, selectRun],
  );
  return { replayingScenarioId, replayError, replay };
}

/** Refresh the catalogue exactly once when a run observed active by this client becomes terminal. */
function useTerminalCatalogueRefresh(
  run: JourneyRunView | null,
  reloadCatalogue: () => void,
): void {
  const observedActive = useRef(new Set<string>());
  useEffect(() => {
    if (run === null) return;
    if (isActive(run.state)) {
      observedActive.current.add(run.runId);
      return;
    }
    if (observedActive.current.delete(run.runId)) reloadCatalogue();
  }, [reloadCatalogue, run]);
}

/** Compose the focused controllers into the render-facing Journey Lab contract. */
export function useJourneyLab(api: JourneyLabApi): JourneyLabController {
  const run = useRunController(api);
  const catalogue = useCatalogueController(api, run);
  const replay = useReplayController(api, run.selectRun, catalogue.reloadCatalogue);
  useTerminalCatalogueRefresh(run.selectedRun, catalogue.reloadCatalogue);
  const selectStartedRun = useCallback(
    (runId: string): void => {
      run.selectRun(runId);
      catalogue.reloadCatalogue();
    },
    [catalogue.reloadCatalogue, run.selectRun],
  );
  return {
    catalogue: catalogue.catalogue,
    catalogueLoading: catalogue.catalogueLoading,
    catalogueError: catalogue.catalogueError,
    selectedRun: run.selectedRun,
    selectedRunId: run.selectedRunId,
    runLoading: run.runLoading,
    runError: run.runError,
    selectRun: run.selectRun,
    replayingScenarioId: replay.replayingScenarioId,
    replayError: replay.replayError,
    replay: replay.replay,
    selectStartedRun,
  };
}
